import {
  setIsAnnouncementsRequested,
  type Announcement,
  setAnnouncements,
  announcementMessageMaxSize,
} from '../../../Store/DataSlice'
import './AnnouncementEdit.scss'
import { useEffect, useMemo, useState } from 'react'
import { TextInput } from '../../../Components/TextInput/TextInput'
import { useLocalization } from '../../../Hooks/useLocalization'
import { Button } from '../../../Components/Button/Button'
import { useDispatch, useSelector } from 'react-redux'
import { setDestinationView } from '../../../Store/InterfaceSlice'
import { type RootState } from '../../../Store/Store'
import { getAuthorizationHeader } from '../../../Orchestration/Authentication'
import { LoadingSpinner } from '../../../Components/LoadingSpinner/LoadingSpinner'
import { DateInput } from '../../../Components/DateInput/DateInput'
import { HtmlTextInput } from '../../../Components/HtmlTextInput/HtmlTextInput'

interface AnnouncementEditProps {
  uuid: string
}
export function AnnouncementEdit({ uuid }: AnnouncementEditProps) {
  const dispatch = useDispatch()
  const localization = useLocalization('announcement')

  const isAnnouncementsRequested = useSelector(
    (state: RootState) => state.data.isAnnouncementsRequested
  )
  useEffect(() => {
    if (!isAnnouncementsRequested) dispatch(setIsAnnouncementsRequested(true))
  }, [isAnnouncementsRequested, dispatch])

  const allAnnouncements = useSelector(
    (state: RootState) => state.data.announcements
  )
  const baseAnnouncement = allAnnouncements.find((c) => c.uuid === uuid)

  const [announcementChange, setAnnouncementChange] = useState<
    Partial<Announcement>
  >({})
  const announcement = useMemo<Announcement | undefined>(
    () =>
      baseAnnouncement === undefined
        ? undefined
        : { ...baseAnnouncement, ...announcementChange },
    [baseAnnouncement, announcementChange]
  )

  const [validationErrors, setValidationErrors] = useState<validationError[]>(
    []
  )
  useEffect(() => {
    const newValidationErrors: validationError[] = []
    if (!announcement) return

    if (announcement.date == null)
      newValidationErrors.push({ property: 'date', type: 'isRequired' })

    if (announcement.message.length > announcementMessageMaxSize)
      newValidationErrors.push({
        property: 'message',
        type: 'length',
      })

    console.log(
      'newValidationErrors',
      JSON.stringify({ newValidationErrors, announcement })
    )
    setValidationErrors(newValidationErrors)
  }, [announcement])

  const [saveInProgress, setSaveInProgress] = useState(false)
  const saveAnnouncement = () => {
    if (announcement == null) return
    setSaveInProgress(true)

    fetch(
      `https://qrzszdu3vd.execute-api.eu-west-1.amazonaws.com/default/announcement-add`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthorizationHeader(),
        },
        body: JSON.stringify({
          announcement,
        }),
      }
    )
      .then(() => {
        dispatch(
          setAnnouncements([
            ...allAnnouncements.filter((c) => c.uuid !== announcement.uuid),
            announcement,
          ])
        )
        dispatch(setIsAnnouncementsRequested(false))
        setTimeout(() => {
          dispatch(setDestinationView({ destinationView: 'announcement-list' }))
        }, 1)
      })
      .catch((err) => {
        console.warn(`fetch announcement-add failed: `, err)
      })
  }

  if (!announcement) return null

  return (
    <div className="announcement-edit">
      {saveInProgress ? (
        <div className="save-in-progress">
          <LoadingSpinner color="dark" />
          {localization.text(
            'announcement-save-in-progress',
            'De aankondiging word momenteel opgeslagen.'
          )}
        </div>
      ) : !baseAnnouncement ? (
        <div className="loading">
          <LoadingSpinner color="dark" />
          {localization.text(
            'announcement-is-loading',
            'De aankondiging word momenteel ingeladen.'
          )}
        </div>
      ) : (
        <>
          <DateInput
            label={localization.text('date', 'Datum')}
            isReadOnly={saveInProgress}
            onChange={(newValue) => {
              if (newValue !== undefined)
                setAnnouncementChange((previous) => ({
                  ...previous,
                  date: newValue,
                }))
            }}
            value={announcement.date}
            isValid={!validationErrors.some((ve) => ve.property === 'date')}
            helperText={
              validationErrors.find((ve) => ve.property === 'date')?.type ===
              'isRequired'
                ? localization.text('date-missing', 'Geef een geldige datum in')
                : undefined
            }
          />

          <TextInput
            label={localization.text('title', 'Titel')}
            isReadOnly={saveInProgress}
            onChange={(value) => {
              setAnnouncementChange((previous) => ({
                ...previous,
                title: value,
              }))
            }}
            value={announcement.title}
            isValid={!validationErrors.some((ve) => ve.property === 'title')}
          />

          <HtmlTextInput
            label={localization.text('message', 'Bericht')}
            isReadOnly={saveInProgress}
            onChange={(value) => {
              setAnnouncementChange((previous) => ({
                ...previous,
                message: value,
              }))
            }}
            value={announcement.message}
            isValid={!validationErrors.some((ve) => ve.property === 'message')}
            helperText={
              validationErrors.find((ve) => ve.property === 'message')?.type ===
              'length'
                ? localization.text(
                    'message-too-long',
                    'Dit bericht is te lang. Mogelijks door te grote afbeeldingen? Maximum 300kb text + afbeeldingen.'
                  )
                : undefined
            }
          />

          <div className="buttons">
            <Button
              importance={'primary'}
              text={localization.text(
                'safe-edit-button',
                'Pas aankondiging aan'
              )}
              onClick={saveAnnouncement}
              disabled={validationErrors.length > 0 || saveInProgress}
            />
          </div>
        </>
      )}
    </div>
  )
}

interface validationError {
  property: string
  type: 'isRequired' | 'format' | 'length'
}
